import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import CTASection from "./CTASection";
import Testimonials from "./Testimonials";

/**
 * Reusable inner page template — covers Hero, Feature Overview, 3 Detailed Features, Use Cases, Testimonials, CTA.
 * Each props payload defines the page's content.
 */
export default function FeaturePage({
  badge,
  title,
  highlight,
  subtitle,
  heroImage,
  heroBg = "cream",
  primaryCta = "Start free trial",
  secondaryCta = "Talk to sales",
  features = [],
  details = [],
  useCases = [],
  ctaTitle,
  ctaSubtitle,
  ctaAccent = "mint",
}) {
  const heroBgMap = {
    cream: "from-[#FFF9E6] via-[#FFFCF2] to-[#FFE4E8]",
    pink: "from-[#FFE4E8] via-[#FFFCF2] to-[#FFF9E6]",
    mint: "from-[#E8F5E9] via-[#FFFCF2] to-[#FFF9E6]",
  };

  return (
    <div data-testid="feature-page">
      {/* Hero */}
      <section className={`relative bg-gradient-to-br ${heroBgMap[heroBg]} overflow-hidden`}>
        <div className="absolute -top-32 left-1/4 w-96 h-96 rounded-full bg-white/40 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 right-1/4 w-96 h-96 rounded-full bg-[#1B4332]/5 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 md:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            {badge && (
              <span className="inline-block px-4 py-1.5 rounded-full bg-white/70 backdrop-blur-md border border-[#1B4332]/10 text-black text-xs font-semibold mb-6">
                {badge}
              </span>
            )}
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold text-black tracking-tight leading-[1.05]">
              {title} {highlight && <span className="italic text-black/60 font-medium">{highlight}</span>}
            </h1>
            <p className="mt-6 text-lg text-black/70 leading-relaxed max-w-lg">{subtitle}</p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link to="/register" className="btn-primary">{primaryCta} <ArrowRight size={18} /></Link>
              <Link to="/support" className="btn-secondary">{secondaryCta}</Link>
            </div>
          </div>
          {heroImage && (
            <div className="animate-fade-up delay-200">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-tr from-[#1B4332]/10 to-transparent rounded-3xl blur-2xl" />
                <img src={heroImage} alt={title} className="relative rounded-3xl shadow-2xl w-full object-cover aspect-[4/3]" />
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Features overview grid */}
      {features.length > 0 && (
        <section className="py-24 md:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="max-w-3xl mb-16">
              <h2 className="font-display text-3xl md:text-5xl font-extrabold text-black tracking-tight">
                Everything you need.<br /><span className="italic text-black/60 font-medium">Nothing you don't.</span>
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((f, i) => (
                <div key={i} className="bg-[#FFFCF2] rounded-3xl p-7 border border-[#1B4332]/8 card-hover">
                  <div className="w-12 h-12 rounded-xl bg-[#1B4332] flex items-center justify-center mb-5 text-white">
                    {f.icon}
                  </div>
                  <h3 className="font-display font-bold text-xl text-black mb-2">{f.title}</h3>
                  <p className="text-black/70 leading-relaxed text-sm">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Detailed split sections */}
      {details.map((d, i) => (
        <section
          key={i}
          className={`py-24 md:py-28 ${
            i % 3 === 0 ? "bg-[#FFF9E6]" : i % 3 === 1 ? "bg-[#FFE4E8]" : "bg-[#E8F5E9]"
          }`}
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
            <div className={i % 2 === 1 ? "lg:order-2" : ""}>
              <span className="inline-block px-3 py-1 rounded-full bg-[#1B4332]/10 text-black text-xs font-semibold mb-4">
                {d.tag || `Feature ${i + 1}`}
              </span>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold text-black leading-tight tracking-tight">
                {d.title}
              </h2>
              <p className="mt-5 text-black/75 text-lg leading-relaxed">{d.desc}</p>
              {d.bullets && (
                <ul className="mt-6 space-y-3">
                  {d.bullets.map((b, j) => (
                    <li key={j} className="flex items-start gap-3">
                      <Check size={20} className="text-black mt-0.5 flex-shrink-0" />
                      <span className="text-black/80">{b}</span>
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-8">
                <Link to="/register" className="text-black font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all">
                  Learn more <ArrowRight size={18} />
                </Link>
              </div>
            </div>
            <div className={i % 2 === 1 ? "lg:order-1" : ""}>
              {d.image ? (
                <img src={d.image} alt={d.title} className="rounded-3xl shadow-xl w-full aspect-[4/3] object-cover" />
              ) : (
                <div className="bg-white rounded-3xl shadow-xl aspect-[4/3] flex items-center justify-center text-black/30 p-8">
                  {d.placeholder || "Product preview"}
                </div>
              )}
            </div>
          </div>
        </section>
      ))}

      {/* Use cases */}
      {useCases.length > 0 && (
        <section className="py-24 md:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="inline-block px-4 py-1.5 rounded-full bg-[#FFF9E6] text-black text-xs font-semibold mb-4">
                Use cases
              </span>
              <h2 className="font-display text-3xl md:text-5xl font-extrabold text-black tracking-tight">
                Built for how you work
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
              {useCases.map((u, i) => (
                <div key={i} className="rounded-3xl p-7 bg-[#FFFCF2] border border-[#1B4332]/8 card-hover">
                  <div className="text-4xl mb-4 font-display font-extrabold text-black/30">{String(i + 1).padStart(2, "0")}</div>
                  <h3 className="font-display font-bold text-lg text-black mb-2">{u.title}</h3>
                  <p className="text-sm text-black/70 leading-relaxed">{u.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <Testimonials />

      <CTASection title={ctaTitle} subtitle={ctaSubtitle} accent={ctaAccent} />
    </div>
  );
}
